// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    console.log('Checking SiriDharini auth status...');
    try {
      // Check for user info from different storage keys
      const isLoggedIn = localStorage.getItem('siridharini_isLoggedIn');
      const userInfo = localStorage.getItem('siridharini_userInfo');
      const siridhariniUser = localStorage.getItem('siridharini_user');

      console.log('Storage check:', { isLoggedIn, userInfo: !!userInfo, siridhariniUser: !!siridhariniUser });

      if (isLoggedIn === 'true' && userInfo) {
        // If logged in via AdminLogin component
        const userData = JSON.parse(userInfo);
        console.log('Found userInfo in localStorage:', userData);
        setUser({
          ...userData,
          role: 'admin' // Assuming admin login always gives admin role
        });
      } else if (siridhariniUser) {
        // If logged in via previous method
        const userData = JSON.parse(siridhariniUser);
        console.log('Found siridhariniUser in localStorage:', userData);
        setUser(userData);
      } else {
        console.log('No authentication data found');
        setUser(null);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setUser(null);
      // Clear corrupted data
      localStorage.removeItem('siridharini_isLoggedIn');
      localStorage.removeItem('siridharini_userInfo');
      localStorage.removeItem('siridharini_user');
    } finally {
      setLoading(false);
      console.log('Auth check completed');
    }
  };

  const login = (userData, rememberMe = false) => {
    console.log('SiriDharini login called with:', userData, rememberMe);
    const user = {
      id: userData.id || 1,
      email: userData.email || userData.username,
      name: userData.name || userData.username,
      role: userData.role || 'admin',
      username: userData.username
    };

    console.log('Setting user:', user);
    setUser(user);

    if (rememberMe) {
      localStorage.setItem('siridharini_isLoggedIn', 'true');
      localStorage.setItem('siridharini_userInfo', JSON.stringify(user));
      console.log('Saved to localStorage with remember me');
    }
    
    // Also set siridharini_user for compatibility
    localStorage.setItem('siridharini_user', JSON.stringify(user));
    console.log('Saved siridharini_user to localStorage');
    
    return { success: true };
  };

  const logout = () => {
    console.log('SiriDharini logout called');
    setUser(null);
    // Clear all auth-related localStorage items
    localStorage.removeItem('siridharini_isLoggedIn');
    localStorage.removeItem('siridharini_userInfo');
    localStorage.removeItem('siridharini_user');
    localStorage.removeItem('siridharini_username');
    console.log('Cleared all SiriDharini auth data from localStorage');
  };

  const isAdmin = () => {
    const result = user?.role === 'admin';
    console.log('isAdmin check:', result, 'user role:', user?.role);
    return result;
  };

  const isAuthenticated = () => {
    const result = !!user;
    console.log('isAuthenticated check:', result, 'user exists:', !!user);
    return result;
  };

  // Function to update auth status (useful for login success)
  const updateAuthStatus = (userData, rememberMe = false) => {
    console.log('updateAuthStatus called');
    return login(userData, rememberMe);
  };

  const value = {
    user,
    login,
    logout,
    isAdmin,
    isAuthenticated,
    loading,
    updateAuthStatus,
    checkAuthStatus
  };

  console.log('SiriDharini AuthProvider rendering with user:', user, 'loading:', loading);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};